/** /academy/account — « My account » (D-110): the Academy Pass and its invoices, one-click
 * renewal, referral, country and time zone, consents, data export and account deletion. */

import {
  ApiError,
  academyAccountExportRetrieve,
  getAcademyAccountRetrieveQueryKey,
  getAcademyMeRetrieveQueryKey,
  useAcademyAccountConsentsCreate,
  useAcademyAccountDeleteCreate,
  useAcademyAccountRetrieve,
  useAcademyMePartialUpdate,
} from "@agencyos/api-client";
import { asConsents, asReferral, asSubscriptions, detectTimeZone, formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton, SoftToggle, TextField, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Gift, Globe2, RefreshCw, Shield, Trash2 } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { session } from "../../lib/session";
import { fieldErrors } from "../../lib/settings";
import { dateText } from "../shared";
import { CardTitle, PageHead } from "../ui";
import { CountrySelect } from "./CountrySelect";

const STATUS_TONE: Record<string, "success" | "default" | "warning" | "danger"> = {
  active: "success",
  pending: "warning",
  refunded: "danger",
  expired: "default",
  cancelled: "default",
};

export function AccountPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const account = useAcademyAccountRetrieve({ query: { retry: false } });
  const data = account.data?.status === 200 ? account.data.data : undefined;
  const rows = asSubscriptions(data?.subscriptions);
  const referral = asReferral(data?.referral);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: getAcademyAccountRetrieveQueryKey() });

  return (
    <div className="ac-page as-account">
      <PageHead eyebrow={t("academySales.pass.eyebrow")} title={t("academySales.account.title")} />
      {account.isLoading ? <Skeleton style={{ height: 260 }} /> : null}
      {account.isError ? <p className="ko">{t("common.error")}</p> : null}
      {data ? (
        <div className="as-account__grid">
          <section className="ac-surface ac-card" aria-labelledby="as-plan-title">
            <CardTitle id="as-plan-title" icon={<Shield size={16} />} tone="accent">
              {t("academySales.account.plan")}
            </CardTitle>
            <p className={data.active_until ? "ac-plan-status__title" : "ac-secondary"}>
              {data.active_until
                ? t("academySales.pass.active", { date: dateText(data.active_until, i18n.language) })
                : t("academySales.account.none")}
            </p>
            <div className="ac-card__actions">
              <Link className={buttonClass("primary")} to={data.can_renew ? "/academy/plans?renew=1" : "/academy/plans"}>
                <RefreshCw aria-hidden size={16} />{" "}
                {data.can_renew ? t("academySales.account.renewOne") : t("academySales.pass.choose")}
              </Link>
            </div>
            <p className="ac-footnote">{t("academySales.renewal.noAuto")}</p>
          </section>

          <section className="ac-surface ac-card as-account__wide" aria-labelledby="as-history-title">
            <CardTitle id="as-history-title" icon={<FileText size={16} />} tone="info">
              {t("academySales.account.history")}
            </CardTitle>
            {rows.length === 0 ? <p className="ac-secondary">{t("academySales.account.empty")}</p> : null}
            {rows.length ? (
              <div className="as-table-wrap">
                <table className="as-table">
                  <thead>
                    <tr>
                      <th scope="col">{t("academySales.admin.period")}</th>
                      <th scope="col">{t("academySales.admin.status")}</th>
                      <th scope="col">{t("academySales.admin.paid")}</th>
                      <th scope="col">{t("academySales.checkout.reference")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.starts_on && row.ends_on
                            ? t("academySales.account.from", {
                                start: dateText(row.starts_on, i18n.language),
                                end: dateText(row.ends_on, i18n.language),
                              })
                            : "—"}
                          <span className="ac-footnote"> · {t(`academySales.account.kind.${row.kind}`)}</span>
                        </td>
                        <td>
                          <Badge tone={STATUS_TONE[row.status] ?? "default"}>{t(`academySales.account.status.${row.status}`)}</Badge>
                        </td>
                        <td className="ao-num">{row.amount_xaf ? formatXaf(row.amount_xaf) : "—"}</td>
                        <td>
                          {row.invoice_url ? (
                            <a className="ac-link" href={row.invoice_url} target="_blank" rel="noreferrer">
                              {row.invoice_number}
                            </a>
                          ) : (
                            row.invoice_number || "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          {referral.enabled ? <ReferralCard code={referral.code} count={referral.count} /> : null}
          <ProfileCard country={data.country} timezone={data.timezone} onSaved={refresh} />
          <PrivacyCard consents={data.consents} onSaved={refresh} />
        </div>
      ) : null}
      <p className="ac-footnote as-account__legal">
        <Link className="ac-link" to="/academy/legal/terms">
          {t("academySales.account.legal")}
        </Link>
      </p>
    </div>
  );
}

function ReferralCard({ code, count }: { code: string; count: number }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/academy?ref=${code}`;
  return (
    <section className="ac-surface ac-card" aria-labelledby="as-ref-title">
      <CardTitle id="as-ref-title" icon={<Gift size={16} />} tone="success">
        {t("academySales.account.referral")}
      </CardTitle>
      <p className="ac-secondary">{t("academySales.account.referralText")}</p>
      <p className="as-code ao-num">{code}</p>
      <p className="ac-footnote">{t("academySales.account.referralCount", { count })}</p>
      <Button
        variant="secondary"
        onClick={() =>
          void navigator.clipboard
            ?.writeText(link)
            .then(() => setCopied(true))
            .catch(() => setCopied(false))
        }
      >
        {copied ? t("academySales.account.copied") : t("academySales.account.copy")}
      </Button>
    </section>
  );
}

function ProfileCard({ country, timezone, onSaved }: { country: string; timezone: string; onSaved: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useAcademyMePartialUpdate();
  const [value, setValue] = useState(country);
  const [zone, setZone] = useState(timezone || detectTimeZone());
  const errors = fieldErrors(save.error);
  useEffect(() => setValue(country), [country]);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { data: { country: value, timezone: zone } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
          onSaved();
        },
      },
    );
  };
  return (
    <section id="profile" className="ac-surface ac-card" aria-labelledby="as-profile-title">
      <form className="ac-form" onSubmit={onSubmit}>
        <CardTitle id="as-profile-title" icon={<Globe2 size={16} />} tone="info">
          {t("academySales.account.profile")}
        </CardTitle>
        <p className="ac-footnote">{t("academySales.account.profileHint")}</p>
        <CountrySelect value={value} onChange={setValue} error={errors.country} />
        <TextField label={t("academySales.account.timezone")} value={zone} onChange={(e) => setZone(e.target.value)} error={errors.timezone} />
        <Button type="submit" variant="secondary" disabled={save.isPending}>
          {t("academySales.account.save")}
        </Button>
        {save.isSuccess ? (
          <p role="status" className="ac-status ac-status--success">
            {t("academySales.account.saved")}
          </p>
        ) : null}
      </form>
    </section>
  );
}

function PrivacyCard({ consents, onSaved }: { consents: unknown; onSaved: () => void }) {
  const { t } = useTranslation();
  const values = asConsents(consents);
  const saveConsent = useAcademyAccountConsentsCreate();
  const remove = useAcademyAccountDeleteCreate();
  const [email, setEmail] = useState("");
  const [exporting, setExporting] = useState(false);
  const toggle = (key: "analytics" | "marketing", value: boolean) =>
    saveConsent.mutate({ data: { [key]: value } }, { onSuccess: onSaved });

  /** The JSON file is read with the session, then saved by the browser. */
  const download = async () => {
    setExporting(true);
    try {
      const response = await academyAccountExportRetrieve();
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "academy-mes-donnees.json";
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const onDelete = (event: FormEvent) => {
    event.preventDefault();
    remove.mutate(
      { data: { email: email.trim() } },
      {
        onSuccess: () => {
          session.signOut();
          window.location.assign("/academy?deleted=1");
        },
      },
    );
  };
  const deleteError = fieldErrors(remove.error).email ?? (remove.error instanceof ApiError ? remove.error.detail : undefined);

  return (
    <section className="ac-surface ac-card as-account__wide" aria-labelledby="as-privacy-title">
      <CardTitle id="as-privacy-title" icon={<Shield size={16} />} tone="warning">
        {t("academySales.account.privacy")}
      </CardTitle>
      <SoftToggle label={t("academySales.account.analytics")} checked={values.analytics} onChange={(v) => toggle("analytics", v)} />
      <SoftToggle label={t("academySales.account.marketing")} checked={values.marketing} onChange={(v) => toggle("marketing", v)} />
      <Button variant="secondary" onClick={() => void download()} disabled={exporting}>
        <Download aria-hidden size={16} /> {t("academySales.account.export")}
      </Button>
      <GlassCard as="div" className="as-danger">
        <form className="ac-form" onSubmit={onDelete}>
          <h3 className="ac-title-3">{t("academySales.account.delete")}</h3>
          <p className="ac-footnote">{t("academySales.account.deleteText")}</p>
          <TextField
            label={t("academySales.account.deleteConfirm")}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={deleteError}
          />
          <Button type="submit" variant="destructive" disabled={!email.trim() || remove.isPending}>
            <Trash2 aria-hidden size={16} /> {t("academySales.account.deleteButton")}
          </Button>
        </form>
      </GlassCard>
    </section>
  );
}
